import { TranslationKey } from '@Localization';
import { inject, injectable, interfaces } from 'inversify';
import { debounce } from 'lodash';
import { action, makeObservable } from 'mobx';
import { v4 } from 'uuid';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { ACDLError, InputStatus } from '../../models/error.model';
import { IMainApiService } from '../../services/api.service';
import { IApiService } from '../../services/api.service.base';
import { Plan } from '../user/user.store';
import { IDatasetDraft, IDatasetDraftsStore } from './datasetDrafts.store';
import { IImagesUploaderService } from './imagesUpload.service';

export interface ICreateDatasetService {
  initNewDraft(): string;
  changeDraftNameAsync(name: string): Promise<void>;
  changeDraftDescription(name: string): void;
  createDatasetFromDraftAsync(): Promise<void | ACDLError>;
  datasetsDraftStore: IDatasetDraftsStore;
  changeDraftAuthor(name: string): void;
  changeDraftTermsOfUse(name: string): void;
}

export namespace ICreateDatasetService {
  export const $: interfaces.ServiceIdentifier<ICreateDatasetService> = Symbol('CREATE_DATASET_SERVICE');
}

@injectable()
export class CreateDatasetService implements ICreateDatasetService {
  constructor(
    @inject(IDatasetDraftsStore.$) readonly datasetsDraftStore: IDatasetDraftsStore,
    @inject(IMainApiService.$) private readonly apiService: IApiService,
    @inject(IImagesUploaderService.$) private readonly imagesService: IImagesUploaderService,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore
  ) {
    makeObservable(this);
  }

  @action
  initNewDraft(): string {
    const newDraft: IDatasetDraft = {
      ownerPlan: Plan.Free,
      workspaceId: this.currentWorkspaceStore.currentWorkspace.id,
      nameStatus: InputStatus.empty(),
      externalStatus: InputStatus.empty(),
      name: `Dataset ${new Date().toLocaleString()}`,
      id: v4(),
      description: '',
      images: [],
      imagesToUploadFromFile: [],
      imagesRejectedFromFile: [],
      willExceedsUploadImageSizeLimit: false,
      imagePreValidationWasSuccessful: true,
      author: '',
      termsOfUse: '',
    };

    this.datasetsDraftStore.draft = newDraft;
    return newDraft.id;
  }

  checkForNameDuplicatates = debounce(async (draft: IDatasetDraft) => {
    const url = '/datasets/CheckDatasetNameUniqueness';
    const params = { datasetId: draft.id, workspaceId: draft.workspaceId, name: draft.name };
    const result = await this.apiService.getAsync<boolean | ACDLError>(url, { params });
    if (result instanceof ACDLError) {
      const errorCodes: TranslationKey[] = result.apiErrorResponse ? result.apiErrorResponse.errorCodes : ['something_went_wrong'];
      draft.externalStatus = InputStatus.buildFrom(errorCodes);
    } else if (!result) {
      draft.nameStatus = InputStatus.buildFrom(['duplicate_dataset_name']);
    }
  }, 500);

  @action.bound
  changeDraftNameAsync = async (name: string): Promise<void> => {
    const draft = this.datasetsDraftStore.draft;
    draft.name = name;
    draft.nameStatus = InputStatus.empty();
    if (name.trim() === '') {
      draft.nameStatus = InputStatus.buildFrom(['field_cant_be_empty']);
    } else {
      await this.checkForNameDuplicatates(draft);
    }
  };

  @action.bound
  changeDraftDescription(description: string): void {
    const draft = this.datasetsDraftStore.draft;
    draft.description = description;
  }

  @action.bound
  changeDraftAuthor(author: string): void {
    const draft = this.datasetsDraftStore.draft;
    draft.author = author;
  }

  @action.bound
  changeDraftTermsOfUse(termsOfUse: string): void {
    const draft = this.datasetsDraftStore.draft;
    draft.termsOfUse = termsOfUse;
  }

  @action.bound
  async createDatasetFromDraftAsync(): Promise<ACDLError | undefined> {
    const url = '/datasets/create';
    const draft = this.datasetsDraftStore.draft;
    const result = await this.apiService.postAsync(url, draft);
    if (result instanceof ACDLError) {
      if (result.isBadRequestWithCode(['DUPLICATE_DATASET_NAME'])) {
        draft.nameStatus = InputStatus.buildFrom(['duplicate_dataset_name']);
      } else {
        const errorCodes: TranslationKey[] = result.apiErrorResponse ? result.apiErrorResponse.errorCodes : ['something_went_wrong'];
        draft.externalStatus = InputStatus.buildFrom(errorCodes);
      }
      return result;
    }

    await this.uploadImagesToDraftAsync(draft.id);
    return Promise.resolve(undefined);
  }

  @action
  async uploadImagesToDraftAsync(draftId: string): Promise<void> {
    const draft = this.datasetsDraftStore.draft;
    if (draft.imagesToUploadFromFile.length > 0) {
      await this.imagesService.addDatasetToQueueAsync(draft.imagesToUploadFromFile, draftId, draft.name);
    }

    draft.imagesToUploadFromFile = [];
    draft.imagesRejectedFromFile = [];
  }
}
